import {  autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import numeral from 'numeral';
import { HPFServiceClient } from '../../services/hpfApi';
import { PayrazrService } from '../../services/payrazr';
import { Autopay, AutopayResponse, Ach, Card, ExpirationType } from '../../models/autopay';
import { UpdateAutopayRequest, CreateAutopayRequest } from '../../models/hpfApi';
import { ApiResponse } from '../../models/api-response';
import { Portfolio } from '../../models/portfolio';
import { FormattingHelper } from '../../helpers/formatting-helper';
import { TemplateData } from '../../models/template-data';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { State } from '../../services/global-state';

@autoinject
export class AutopayService {
	autopay: Autopay;
	formattedConvenienceFee: string;
	isTieredFees: boolean;
	savedToWallet: boolean = false;

	constructor(
		private i18n: I18N,
		private payrazrApi: PayrazrService,
		private hpfApi: HPFServiceClient,
		private globalState: State
	) { }

	getLatestAutopay(customerAccountKey: number): Promise<Autopay> {
		return this.hpfApi.getAutopayByAccount(customerAccountKey.toString())
			.then((result) => {
				if (result.isSuccessful) {
					return this.autopay = this.parseAutopayResponse(result.value, customerAccountKey);
				} else {
					return null;
				}
			});
	}

	getUpdatedAutopay(): Promise<ApiResponse> {
		return this.hpfApi.getAutopayByKey(this.autopay.key.toString());
	}

	updateAutopay(autopayKey: number, method: Ach | Card, maxPaymentAmount?: number, payInFull?: boolean): Promise<ApiResponse> {
		const request = new UpdateAutopayRequest(
			method,
			maxPaymentAmount,
			payInFull
		);
		return this.hpfApi.updateAutopay(autopayKey.toString(), request);
	}

	createAutopay(autopay: Autopay): Promise<ApiResponse> {
		this.autopay = autopay; // store the autopay for use in receipt page
		const request = new CreateAutopayRequest(
			autopay.customerAccountKey,
			autopay.frequency.autopayTriggerType,
			autopay.frequency.autopayTriggerType === 5 ? autopay.dayOfMonth : autopay.frequency.value,
			this.getExpirationType(autopay),
			autopay.maxPaymentAmount,
			autopay.payInFull,
			autopay.paymentMethod,
			autopay.overriddenFeeAmount,
			autopay.numberOfPayments,
			autopay.endDate
		);
		return this.hpfApi.createAutopay(request);
	}

	parseAutopayResponse(autopayResponse: AutopayResponse, customerAccountKey: number): Autopay {
		let autopay = new Autopay();
		autopay.customerAccountKey = customerAccountKey;
		autopay.endDate = autopayResponse.endDate ? new Date(autopayResponse.endDate) : null;
		autopay.frequency = autopayResponse.trigger;
		autopay.key = autopayResponse.autopayKey;
		autopay.maxPaymentAmount = autopayResponse.maxPaymentAmount;
		autopay.numberOfPayments = autopayResponse.numberOfPayments;
		autopay.payInFull = autopayResponse.willPayInFull;
		autopay.paymentMethod = autopayResponse.paymentMethod;
		autopay.startDate = new Date(autopayResponse.startDate);
		autopay.feeCalculationType = autopayResponse.feeCalculationType;
		autopay.feeValue = autopayResponse.feeValue;
		autopay.feeValues = autopayResponse.feeValues;
		return autopay;
	}

	setFeeSettings(autopay: Autopay, isFeeEnabled: boolean) {
		if (autopay && isFeeEnabled) {
			// overridden fee
			if (autopay.feeValue !== null) {
				// overridden fees are always fixed
				this.formattedConvenienceFee = numeral(autopay.feeValue).format('($0,0.00)');
			}
			// single fee
			else if (autopay.feeValues !== null && autopay.feeValues.length > 0 && autopay.feeValues.every(fv => fv.tierLimitKey === 1)) {
				let tier1FeeValue = autopay.feeValues.find(fv => fv.tierLimitKey === 1);

				this.formattedConvenienceFee = tier1FeeValue.feeCalculationType === FeeCalculationType.Percentage
					? `${numeral(tier1FeeValue.feeValue).format('0.[0000]')}%`
					: numeral(tier1FeeValue.feeValue).format('($0,0.00)');
			}
			// tiered fees
			else {
				this.formattedConvenienceFee = null;
			}
		}
	}

	getConfirmationTemplate(data: any): Promise<ITemplateResponse> {
		let templateData: TemplateData = new TemplateData(data);
		return this.payrazrApi.applyTemplateAndReturnHash('Autopay Plan Confirmation', templateData);
	}

	getStartDate(): string {
		let startDate = '';

		if (this.autopay && this.autopay.startDate) {
			startDate = new Date(this.autopay.startDate).toLocaleDateString();
		}

		return startDate;
	}

	getEndDate(): string {
		let endDate = 'N/A';

		if (this.autopay) {
			if (this.autopay.endDate) {
				endDate = new Date(this.autopay.endDate).toLocaleDateString();
			} else if (!this.autopay.numberOfPayments) {
				endDate = this.i18n.i18next.t('indefinite', 'Indefinite');
			}
		}

		return endDate;
	}

	getPaymentLimit(): string {
		let limit: string = '';

		if (!this.autopay) {
			return limit;
		}

		if (this.autopay.payInFull) {
			limit = this.i18n.i18next.t('payInFull', 'Pay In Full');
		} else if (this.autopay.maxPaymentAmount) {
			limit = FormattingHelper.formatCurrency(this.autopay.maxPaymentAmount);
		}

		return limit;
	}

	getExpirationType(autopay: Autopay): ExpirationType {
		let type = null;

		if (autopay.endDate) {
			type = ExpirationType.EndDate;
		} else if (autopay.numberOfPayments) {
			type = ExpirationType.NumberOfPayments;
		} else {
			type = ExpirationType.Indefinite;
		}

		return type;
	}

	getIsTieredFees() {
		return this.autopay.overriddenFeeAmount == null && this.autopay.feeValues.some(fv => fv.tierLimitKey > 1);
	}
}

export interface ITemplateResponse {
	templateHtml: string;
	hashedValue: string;
}
